import type { NotificationData } from '@mantine/notifications';

import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { t } from 'i18next';
import { hapticNotify } from '@/utils/haptics';
import classes from './successPill.module.css';

type TypedNotificationData = NotificationData & {
	type: 'success' | 'error' | 'warning' | 'info';
};

type SuccessNotificationData = Pick<NotificationData, 'message'>;

let visibleSuccessId: string | undefined;

function show({ id, title, message, type, ...rest }: TypedNotificationData) {
	if (type === 'success') {
		showSuccess({ message });
		return;
	}

	const colorMap: Record<string, string> = {
		error: 'red',
		warning: 'yellow',
		info: 'blue',
	};

	const resolvedTitle = title || t(`notifications.${type}.title`);

	if (type !== 'info') hapticNotify(type);

	notifications.show({
		id,
		title: resolvedTitle,
		message,
		color: colorMap[type],
		autoClose: 3000,
		...rest,
	});
}

// Successes are a quiet pill rather than a toast. Mantine ignores a show() for an id that is
// already visible, so a repeat success replaces the pill instead of stacking a second one.
function showSuccess({ message }: SuccessNotificationData) {
	hapticNotify('success');

	if (visibleSuccessId) notifications.hide(visibleSuccessId);

	visibleSuccessId = notifications.show({
		message,
		icon: <IconCheck size={16} />,
		withCloseButton: false,
		autoClose: 2000,
		classNames: classes,
	});
}

function showError({ title = t('notifications.error.title'), ...rest }: NotificationData) {
	show({
		title,
		type: 'error',
		...rest,
	});
}

const defaultNotifications = {
	show,
	showSuccess,
	showError,
};

export default defaultNotifications;
